import { useEffect, useRef, type RefObject } from 'react';
import { useFocusRegistry, type FocusZoneId } from './focusRegistry';

export interface RegisterFocusZoneOptions {
  label: string;
  order: number;
  isVisible: () => boolean;
  /**
   * Optional custom focus. When provided it REPLACES the default
   * last-focused / `primarySelector` DOM `.focus()` behaviour. The tools zone
   * needs this: its focusable target is an xterm reachable only through the
   * active tool's imperative focus handle, not a `.focus()` on the container
   * (a plain `<div>` that the default selector would no-op on).
   */
  focus?: () => void;
}

/**
 * Register a focus zone. The zone's root element should carry `data-focus-zone={id}`
 * so CSS can target the focused / unfocused state.
 *
 * `primarySelector` is queried as a fallback when the last-focused descendant
 * has unmounted; it should target a focusable element inside the zone root.
 */
export function useRegisterFocusZone(
  rootRef: RefObject<HTMLElement | null>,
  id: FocusZoneId,
  primarySelector: string,
  opts: RegisterFocusZoneOptions,
): void {
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Track last-focused descendant so re-focusing the zone restores prior cursor.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onFocusIn = (e: FocusEvent) => {
      lastFocusedRef.current = e.target as HTMLElement;
      useFocusRegistry.setState({ currentZoneId: id });
    };
    root.addEventListener('focusin', onFocusIn);
    return () => root.removeEventListener('focusin', onFocusIn);
  }, [rootRef, id]);

  // Register / unregister.
  useEffect(() => {
    const unregister = useFocusRegistry.getState().register({
      id,
      label: optsRef.current.label,
      order: optsRef.current.order,
      isVisible: () => optsRef.current.isVisible(),
      focus: () => {
        const custom = optsRef.current.focus;
        if (custom) {
          custom();
          return;
        }
        const root = rootRef.current;
        if (!root) return;
        const last = lastFocusedRef.current;
        if (last && root.contains(last) && document.body.contains(last)) {
          last.focus();
          return;
        }
        const primary = root.querySelector<HTMLElement>(primarySelector);
        primary?.focus();
      },
    });
    return unregister;
  }, [rootRef, id, primarySelector]);
}
